import { useState } from 'react'
import { ArrowRight, ExternalLink, HandCoins, LoaderCircle, MapPin, Phone, Scale } from 'lucide-react'
import type { LanguagePack } from '../i18n/packs'
import { currentPosition, LEGAL_HELPLINE, LEGAL_TYPES, mapsUrl, placesFor, SCHEME_TYPES } from '../services/nearby'
import type { StructuredDocument } from '../types/document'

/** Asks the Ask panel a question (it handles consent and the answer). */
export function askSamjho(question: string) {
  document.getElementById('ask')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  window.dispatchEvent(new CustomEvent('samjho-ask', { detail: question }))
}

/** "Get help": nearest office on the map, schemes that may fit, and free legal aid. */
export function HelpPanel({ p, doc, canAsk, onOpenLegal }: { p: LanguagePack; doc: StructuredDocument; canAsk: boolean; onOpenLegal: () => void }) {
  const [locating, setLocating] = useState<string | null>(null)
  const places = placesFor(doc)
  const schemes = canAsk && SCHEME_TYPES.includes(doc.documentType)
  const legal = LEGAL_TYPES.includes(doc.documentType)

  async function openMap(query: string) {
    // Open the tab now (inside the tap), so pop-up blockers allow it; point it at the map once we know where we are
    const tab = window.open('about:blank', '_blank')
    setLocating(query)
    const position = await currentPosition()
    setLocating(null)
    const url = mapsUrl(query, position)
    if (tab) {
      tab.opener = null
      tab.location.href = url
    } else {
      window.location.href = url
    }
  }

  return (
    <section className="help-panel" aria-labelledby="help-title">
      <h2 id="help-title">{p.helpTitle}</h2>

      <div className="help-card">
        <h3>
          <MapPin size={20} aria-hidden="true" /> {p.nearbyTitle}
        </h3>
        <div className="help-actions">
          {places.map((place) => (
            <button key={place.query} className="inline-action" onClick={() => void openMap(place.query)} disabled={locating !== null}>
              {locating === place.query ? <LoaderCircle className="spin" size={16} aria-hidden="true" /> : <MapPin size={16} aria-hidden="true" />}
              {locating === place.query ? p.locating : place.label}
            </button>
          ))}
        </div>
        <p className="help-note">{p.nearbyNote}</p>
      </div>

      {schemes && (
        <div className="help-card">
          <h3>
            <HandCoins size={20} aria-hidden="true" /> {p.schemesTitle}
          </h3>
          <p>{p.schemesText}</p>
          <div className="help-actions">
            <button className="inline-action" onClick={() => askSamjho(p.askSuggest.schemes)}>
              <HandCoins size={16} aria-hidden="true" /> {p.schemesButton}
            </button>
          </div>
        </div>
      )}

      {legal && (
        <div className="help-card help-legal">
          <h3>
            <Scale size={20} aria-hidden="true" /> {p.legalTitle}
          </h3>
          <p>{p.legalText}</p>
          <div className="help-actions">
            <a className="inline-action" href={`tel:${LEGAL_HELPLINE}`}>
              <Phone size={16} aria-hidden="true" /> {p.legalCall(LEGAL_HELPLINE)}
            </a>
            <a className="inline-action" href="https://nalsa.gov.in/legal-aid/" target="_blank" rel="noopener noreferrer">
              <ExternalLink size={16} aria-hidden="true" /> nalsa.gov.in
            </a>
            <button className="inline-action" onClick={() => void openMap('District Legal Services Authority')} disabled={locating !== null}>
              <MapPin size={16} aria-hidden="true" /> {p.legalNearby}
            </button>
            <button className="inline-action" onClick={onOpenLegal}>
              <ArrowRight size={16} aria-hidden="true" /> {p.legalMore}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
